/**
 * Test-phase role switcher — bottom-left control that mints a real session for another
 * seeded persona. Hidden when `GET /api/dev/personas` is unavailable (production default).
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, ApiError } from "@/lib/api";
import { useSession, type Role } from "@/lib/session";
import type { SessionResponse } from "@/shared/schemas/auth";

interface PersonasResponse {
  enabled: boolean;
  personas: { role: Role; email: string; firstName: string; lastName: string }[];
}

type SwitchResponse = SessionResponse & { homePath: string };

async function fetchPersonas(): Promise<PersonasResponse | null> {
  try {
    return await api.get<PersonasResponse>("/dev/personas");
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.code === "NOT_FOUND")) {
      return null;
    }
    throw error;
  }
}

export function RoleSwitcher({
  className = "",
  floating = false,
}: {
  className?: string;
  /** Fixed bottom-left chrome for shells without a sidebar. */
  floating?: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useSession();
  const query = useQuery({
    queryKey: ["dev", "personas"],
    queryFn: fetchPersonas,
    staleTime: 60_000,
    // Dev API may still be booting when the SPA first mounts (Playwright webServer).
    retry: 3,
    retryDelay: 500,
  });

  const personas = query.data?.personas;
  // Hidden only when the endpoint is disabled (404) or no personas exist — not while loading.
  if (query.isError || query.data === null) return null;
  if (!query.isLoading && (!personas || personas.length === 0)) return null;

  const current = user?.role ?? "";
  const ready = Boolean(personas && personas.length > 0);

  async function onRoleChange(role: string) {
    if (!role || role === current) return;
    const result = await api.post<SwitchResponse>("/dev/switch-persona", { role });
    window.location.assign(result.homePath || "/");
  }

  return (
    <div
      className={
        floating
          ? `fixed bottom-4 left-4 z-50 w-44 space-y-1 rounded-md border border-border bg-background/95 p-2 shadow-sm backdrop-blur ${className}`
          : `space-y-1 ${className}`
      }
      data-testid="role-switcher"
      title={t("roleSwitcher.hint")}
    >
      <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        {t("roleSwitcher.label")}
      </div>
      <Select
        value={ready && current ? current : ""}
        onValueChange={(value) => void onRoleChange(value)}
        disabled={!ready}
      >
        <SelectTrigger className="h-8 w-full text-xs" aria-label={t("roleSwitcher.label")}>
          <SelectValue placeholder={t("roleSwitcher.label")} />
        </SelectTrigger>
        <SelectContent>
          {(personas ?? []).map((persona) => (
            <SelectItem key={persona.role} value={persona.role} className="text-xs">
              {t(`roleSwitcher.roles.${persona.role}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
